import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export const getQueryString = (name) => {
  name = name.replace(/[\[]/, "\\[").replace(/[\]]/, "\\]");
  let url = location;
  if (!url) {
    url = window.location.href;
  }
  const regexS = "[\\?&]" + name + "=([^&#]*)";
  const regex = new RegExp(regexS);
  const results = regex.exec(url);
  if (results == null) {
    return "";
  } else {
    return decodeURIComponent(results[1].replace(/\+/g, " "));
  }
};

export const spiltNumber = (num) => {
  const splitNum = num.split(" ");
  const countryCode = splitNum.shift();
  const number = splitNum.join("");
  return { number, countryCode };
};

export const getCurrentDate = () => {
  const currentDate = new Date();
  const day = String(currentDate.getDate()).padStart(2, "0");
  const month = String(currentDate.getMonth() + 1).padStart(2, "0");
  const year = currentDate.getFullYear();
  return `${month}-${day}-${year}`;
};

export const clearInCompletedOrderCache = () => {
  const keys = [
    "incompleteorderData",
    "selectedDoc",
    "selectedMode",
    "SelectedState",
    "propertyId",
    "autofillObject",
    "incompleteorder",
    "selectedFormId",
    "selectedForm",
  ];
  keys.forEach((key) => localStorage.removeItem(key));
};

export const islengthMatch = (field, value) => {
  if (field?.min_date) {
    const currentYear = new Date().getFullYear();
    return (
      value >= field?.min_date &&
      value <= currentYear &&
      value?.length >= field?.min &&
      value?.length <= field?.max
    );
  } else if ((field?.min || field?.min === 0) && field.max) {
    if (field.type !== "PHONE_NUMBER") {
      return value?.length >= field?.min && value?.length <= field?.max;
    } else {
      const splitNum = value.split(" ");
      splitNum.shift();
      const number = splitNum.join("");
      return number?.length >= field?.min && number?.length <= field?.max;
    }
  } else {
    return value.length > 0;
  }
};

// A field's `pattern` from the document JSON, as a RegExp. The JSON writes it
// either as a literal ("/^\\d{0,6}$/", flags allowed) or bare ("^[0-9]+$").
// A pattern that doesn't compile is ignored (null) rather than blocking input.
export const parseFieldPattern = (pattern) => {
  if (!pattern || typeof pattern !== "string") return null;
  const literal = /^\/(.*)\/([a-z]*)$/.exec(pattern);
  try {
    return literal ? new RegExp(literal[1], literal[2]) : new RegExp(pattern);
  } catch {
    return null;
  }
};

// Plain-words message for a pattern the value didn't match: "Enter a
// 10-digit number", "Enter up to 6 digits", … Falls back to the field's own
// errorMessage, then a generic line.
const describePattern = (pattern, field) => {
  const body = String(pattern).replace(/^\/|\/[a-z]*$/g, "");
  const digits = /^\^(?:\\d|\[0-9\])\{(\d+)(?:,(\d*))?\}\$$/.exec(body);
  if (digits) {
    const [, a, b] = digits;
    if (b === undefined || a === b) return `Enter a ${a}-digit number.`;
    if (Number(a) === 0) return `Enter numbers only, up to ${b} digits.`;
    return `Enter ${a} to ${b} digits.`;
  }
  if (/^\^\[0-9\]\+|^\^\[0-9\]\{1,/.test(body)) return "Enter a valid number.";
  const alnum = /^\^\[a-zA-Z0-9-\]\{(\d+)(?:,(\d+))?\}\$$/.exec(body);
  if (alnum) {
    const [, a, b] = alnum;
    return b && a !== b
      ? `Use ${a} to ${b} letters or numbers.`
      : `Use ${a} letters or numbers.`;
  }
  const upTo = /^\^\(?\.\{0,(\d+)\}\)?\$$/.exec(body);
  if (upTo) return `Keep it to ${upTo[1]} characters or fewer.`;
  const name = String(field?.name || "").split("|")[0].trim();
  return field?.errorMessage || field?.validation?.errorMessage || `Enter a valid ${name || "value"}.`;
};

// The error to show for a typed value, or "" when it's fine. Only text and
// number boxes are checked (dates and pickers bring their own format), and an
// empty box is left to the "required" rule.
export const getPatternError = (field, value) => {
  if (field?.type !== "NUMBER" && field?.type !== "STRING") return "";
  const text = value === null || value === undefined ? "" : String(value).trim();
  if (!text) return "";
  const pattern = field?.pattern || field?.validation?.pattern;
  const re = parseFieldPattern(pattern);
  if (!re || re.test(text)) return "";
  return describePattern(pattern, field);
};

export const getCheckboxesPrice = (selectedServices, prices) => {
  return selectedServices?.reduce((sum, selected) => {
    const matchedPrice = prices.find((price) => price.id === selected.id);
    return sum + (matchedPrice?.amount || 0);
  }, 0);
};

// Is a form entry actually filled? Presence of the entry isn't enough: clearing
// a SELECT sends `null` through handleInputChange, and `typeof null === "object"`
// puts it down the object branch, which leaves behind a truthy `{name, id,
// value: null}` wrapper. A cleared dependent field is left as "". Both must read
// as empty, or a cancelled District keeps Mandal unlocked.
export const hasFieldValue = (entry) => {
  if (!entry) return false;
  const value =
    typeof entry === "object" && !Array.isArray(entry) && "value" in entry
      ? entry.value
      : entry;
  if (value === null || value === undefined) return false;
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
};

/**
 * Fields whose option `code` is an IDENTIFIER, not something a customer or an
 * admin would ever quote — so the pickers, chips and order views show the name
 * alone. Hydra/FTL lakes carry HMDA's LakeID, which lands in the order record
 * and the `?lake_code=` deep link but means nothing on screen.
 *
 * Keyed by FIELD ID on purpose: the previous rule sniffed for 24-char hex
 * strings, which silently stopped hiding anything the moment the lake code
 * changed shape. Add a field here rather than pattern-matching its ids.
 */
export const CODE_HIDDEN_FIELDS = ["LAKE_NAME"];

/**
 * Whether an option's `code` earns a spot next to its `name`. Hidden for the
 * fields above, and for a code that merely repeats the name — the HMDA
 * district/mandal/village cascade is keyed by place name, so it sends
 * `code === name` and would otherwise render "Rangareddy (Rangareddy)".
 */
export const isCodeWorthShowing = (fieldId, option) => {
  if (option?.code == null || option.code === "") return false;
  if (CODE_HIDDEN_FIELDS.includes(fieldId)) return false;
  const name = option?.name == null ? "" : String(option.name);
  return String(option.code).trim() !== name.trim();
};

/**
 * One order-input field's value as display text.
 *
 * Order inputs are heterogeneous: a plain string/number, a single
 * `{ name, code }` from a SELECT, or an ARRAY of `{ name, code }` from a
 * MULTI_SELECT (e.g. the lakes on a per-lake-priced Hydra/FTL order). The
 * order-history views used to test `typeof value === "object"` and read
 * `.name` off it, which silently rendered an EMPTY string for the array case —
 * so a multi-lake order showed a blank Lake Name. Arrays are handled here
 * first, before the single-object branch.
 *
 * Returns "" when there is nothing to show, so callers can fall back to "N/A".
 */
export const formatOrderInputValue = (value, fieldId) => {
  const showCodeFor = (item) => isCodeWorthShowing(fieldId, item);

  const one = (item) => {
    if (item === null || item === undefined) return "";
    if (typeof item === "object") {
      const name = item?.name ? String(item.name) : "";
      const code = showCodeFor(item) ? ` (${item.code})` : "";
      // An entry with only a code and no name still shows the code rather than
      // rendering as blank.
      if (!name) return item?.code != null ? String(item.code) : "";
      return `${name}${code}`.trim();
    }
    return String(item);
  };

  if (Array.isArray(value)) {
    return value.map(one).filter(Boolean).join(", ");
  }
  return one(value);
};

export const isSelectFieldDisabled = (field, requestFormData) => {
  if (!(field?.searchFields?.length > 0)) return false;
  return field.searchFields.some(
    (item) => !hasFieldValue(requestFormData?.[item]),
  );
};

// A field's display name, falling back to a readable form of its id
// ("SURVEY_NO" -> "Survey No") when the config carries no name.
export const getFieldDisplayName = (id, allFields) =>
  allFields?.find((f) => f?.id === id)?.name ||
  String(id || "")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

// The copy a locked dependent field shows when tapped. Callers pass the parents
// they found missing, so each screen keeps its own notion of "filled" (the crowd
// screens key off `sel.code`, the request forms off the `{value}` wrapper) while
// the wording stays identical everywhere.
export const buildDependencyMessage = (missingNames, fieldName) => {
  if (!missingNames?.length) return "";
  const list =
    missingNames.length > 1
      ? `${missingNames.slice(0, -1).join(", ")} and ${
          missingNames[missingNames.length - 1]
        }`
      : missingNames[0];
  return `Please select ${list} before selecting ${fieldName || "this field"}`;
};

// A dependent field (MANDAL needs DISTRICT, VILLAGE needs both) can only be
// searched once its parents are filled. Names the still-empty parents so the
// field can tell the user which one to pick first instead of just sitting dead.
export const getMissingSearchFields = (field, requestFormData, allFields) =>
  (field?.searchFields || [])
    .filter((id) => !hasFieldValue(requestFormData?.[id]))
    .map((id) => getFieldDisplayName(id, allFields));

export const getDependencyMessage = (field, requestFormData, allFields) =>
  buildDependencyMessage(
    getMissingSearchFields(field, requestFormData, allFields),
    field?.name,
  );
